import { BookOpen } from 'lucide-react'
import { ShareTriageView } from './ShareTriageView'
import { ShareSurgeryPanel } from './ShareSurgeryPanel'
import type { AtomicBreakdownEntry } from '@/types/api'
import type { ShareSnapshotSource } from '@/types/share'

interface ShareRightPanelProps {
  entries: AtomicBreakdownEntry[]
  sourceById: Map<string, ShareSnapshotSource>
  /** ``null`` shows ShareTriageView; a valid index shows ShareSurgeryPanel
   *  for that claim. Lifted to ``ShareView`` so citation clicks in the
   *  document can drive it. */
  surgeryClaimIndex: number | null
  onSelectClaim: (claimIndex: number | null) => void
}

/**
 * Right-column container for the public share view, mirroring the
 * authenticated app's ``RightPanel`` (320px wide, ``h-12`` header).
 * Toggles between read-only Triage and Surgery based on
 * ``surgeryClaimIndex``.
 */
export function ShareRightPanel({
  entries,
  sourceById,
  surgeryClaimIndex,
  onSelectClaim,
}: ShareRightPanelProps) {
  const activeEntry = surgeryClaimIndex !== null ? entries[surgeryClaimIndex] : null

  return (
    <aside className="w-[320px] max-w-[320px] shrink-0 border-l border-border bg-white flex flex-col h-full overflow-hidden">
      <div className="h-12 flex items-center justify-between px-4 border-b border-border shrink-0">
        <div className="flex items-center gap-2">
          <BookOpen className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
          <h2 className="text-sm font-semibold text-foreground">Review</h2>
        </div>
      </div>

      <div className="flex-1 min-h-0 min-w-0 flex flex-col overflow-hidden">
        {activeEntry ? (
          <ShareSurgeryPanel
            entry={activeEntry}
            sourceById={sourceById}
            onBack={() => onSelectClaim(null)}
          />
        ) : (
          <ShareTriageView
            entries={entries}
            selectedClaimIndex={surgeryClaimIndex}
            onSelectClaim={(i) => onSelectClaim(i)}
          />
        )}
      </div>
    </aside>
  )
}
